import { Suspense } from 'react';
import { usePlugins } from '@/hooks/config/usePlugins';
import type { ResolvedNavUtilityElement } from '@/lib/plugin-registry';

/**
 * The plugin half of the nav's utility row, rendered immediately after the
 * Inbox button in every nav style (collapsed rail, expanded rail, sessions
 * dock). Presentational on purpose so stories can drive it with stand-in
 * elements; {@link NavUtilityElements} is the wired container.
 *
 * Each element owns its own button, tooltip, and live state — the host
 * only decides where the row sits and whether it is icon-only.
 */
export function NavUtilityRow({
  elements,
  compact = false,
}: {
  elements: readonly ResolvedNavUtilityElement[];
  /** Icon-only surfaces (collapsed rail, dock strip) pass `true`. */
  compact?: boolean;
}) {
  if (elements.length === 0) return null;
  return (
    <Suspense fallback={null}>
      {elements.map(({ id, pluginName, component: Element }) => (
        <Element key={`${pluginName}:${id}`} compact={compact} />
      ))}
    </Suspense>
  );
}

/** Plugin-contributed utility icons for the active org, beside the Inbox button. */
export function NavUtilityElements({ compact = false }: { compact?: boolean }) {
  const { navUtilityElements } = usePlugins();
  return <NavUtilityRow elements={navUtilityElements} compact={compact} />;
}
